/**
 * Geo-restriction context provider.
 *
 * Calls GET /api/v1/geo/ on mount to detect the user's country and tier.
 * Caches the result in localStorage to avoid repeated API calls.
 * Exposes tier info and feature-check helpers to all components.
 *
 * NOTE: The useGeoRestriction hook is exported from hooks/useGeoRestriction.ts
 * for React Fast Refresh compatibility.
 */

import React, { createContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { buildApiUrl } from '../services/urlResolver';
import { STORAGE_KEYS } from '../constants/storage';
import type { GeoTier, GeoFeature } from '../constants/geoRestriction';
import { isFeatureAllowedForTier } from '../constants/geoRestriction';

// =============================================================================
// TYPES
// =============================================================================

export interface IGeoContext {
  /** Detected ISO 3166-1 alpha-2 country code */
  countryCode: string;
  /** Current geo tier */
  tier: GeoTier;
  /** Whether geo info is still loading */
  loading: boolean;
  /**
   * True when the geo API call failed (network error, server error, etc.).
   * When true, `tier` is the fail-closed default ('restricted') — not the
   * user's real tier. UI should surface a "couldn't verify region" message
   * with a retry option rather than silently blocking features.
   */
  geoError: boolean;
  /** Check if a specific feature is allowed */
  isFeatureAllowed: (feature: GeoFeature) => boolean;
  /** Whether user is in restricted tier */
  isRestricted: boolean;
  /** Whether user is in minimum tier */
  isMinimum: boolean;
  /** List of features blocked for current tier */
  restrictedFeatures: GeoFeature[];
}

interface CachedGeoData {
  countryCode: string;
  tier: GeoTier;
  restrictedFeatures: GeoFeature[];
  cachedAt: number;
}

// Cache for 1 hour (ms)
const GEO_CACHE_TTL_MS = 60 * 60 * 1000;

// =============================================================================
// CONTEXT
// =============================================================================

// eslint-disable-next-line react-refresh/only-export-components
export const GeoContext = createContext<IGeoContext | undefined>(undefined);

interface GeoProviderProps {
  children: ReactNode;
}

export const GeoProvider: React.FC<GeoProviderProps> = ({ children }) => {
  const [countryCode, setCountryCode] = useState<string>('');
  const [tier, setTier] = useState<GeoTier>('restricted');
  const [restrictedFeatures, setRestrictedFeatures] = useState<GeoFeature[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [geoError, setGeoError] = useState<boolean>(false);

  const isFeatureAllowed = useCallback(
    (feature: GeoFeature): boolean => {
      return isFeatureAllowedForTier(feature, tier);
    },
    [tier]
  );

  useEffect(() => {
    const fetchGeoInfo = async () => {
      // Check localStorage cache first
      try {
        const cached = localStorage.getItem(STORAGE_KEYS.GEO_TIER);
        if (cached) {
          const data: CachedGeoData = JSON.parse(cached);
          const age = Date.now() - data.cachedAt;
          if (age < GEO_CACHE_TTL_MS) {
            setCountryCode(data.countryCode);
            setTier(data.tier);
            setRestrictedFeatures(data.restrictedFeatures);
            setLoading(false);
            return;
          }
        }
      } catch {
        // Ignore cache parse errors
      }

      // Fetch from API
      try {
        const url = buildApiUrl('/api/v1/geo/');
        const response = await fetch(url);
        if (response.ok) {
          const json = await response.json();
          if (json.success && json.data) {
            const data = json.data;
            const detectedTier = (data.tier as GeoTier) || 'restricted';
            const features = (data.restricted_features as GeoFeature[]) || [];

            setCountryCode(data.country_code || '');
            setTier(detectedTier);
            setRestrictedFeatures(features);
            setGeoError(false);

            // Cache in localStorage
            const cacheData: CachedGeoData = {
              countryCode: data.country_code || '',
              tier: detectedTier,
              restrictedFeatures: features,
              cachedAt: Date.now(),
            };
            localStorage.setItem(STORAGE_KEYS.GEO_TIER, JSON.stringify(cacheData));
          }
        } else {
          // Non-2xx response: fail closed (restricted) and surface the error
          console.warn('[Geo] Geo API returned non-ok status:', response.status);
          setGeoError(true);
        }
      } catch (error) {
        // Network error: fail closed (restricted) and surface the error so the
        // UI can show "couldn't verify region" instead of silently blocking.
        console.warn('[Geo] Failed to fetch geo info:', error);
        setGeoError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchGeoInfo();
  }, []);

  const value: IGeoContext = {
    countryCode,
    tier,
    loading,
    geoError,
    isFeatureAllowed,
    isRestricted: tier === 'restricted',
    isMinimum: tier === 'minimum',
    restrictedFeatures,
  };

  return <GeoContext.Provider value={value}>{children}</GeoContext.Provider>;
};
